import { AuviousRtcService } from "./rtc.service";
import {
  IRecorderOptions,
  IRecorderResponse,
  IRecorderInfo,
  IRecorderSessionInfo,
  ITranscript,
  IRecorderCobrowseInfo,
  IRecorderCobrowseUrl,
  IStorageTestSummary,
  IStorageStatusResponse,
  RecorderStateEnum,
} from "../models";
import { ApiResource } from "@auvious/common";
import { Injectable, inject } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { IHook } from "../models/interfaces";
import { debugError } from "../../app/app.utils";
import { ApplicationService } from "./application.service";

/**
 * A recorder that is either recording or about to, so it should be used instead of starting a new one.
 */
export function isRecorderInUse(recorder: IRecorderInfo): boolean {
  return (
    recorder?.state === RecorderStateEnum.active ||
    recorder?.state === RecorderStateEnum.initialised
  );
}

export function isSameRecorder(a: IRecorderInfo, b: IRecorderInfo): boolean {
  return (
    !!a?.recorderId &&
    a.recorderId === b?.recorderId &&
    a.instanceId === b?.instanceId
  );
}

@Injectable()
export class RecorderService {
  private rtcService = inject(AuviousRtcService);
  private application = inject(ApplicationService);

  private recorderResource: ApiResource;
  private recorderEventSubject: Subject<IRecorderInfo> = new Subject();
  private preStopHooks: IHook[] = [];
  // activation requests run one at a time, in the order they were issued
  private activationQueue: Promise<unknown> = Promise.resolve();
  private currentConferenceId: string;

  constructor() {
    this.rtcService.common$.subscribe((common) => {
      this.recorderResource = common.apiResourceFactory("rtc-recorder/api");
    });
  }

  public propagateEvent(event: IRecorderInfo) {
    this.recorderEventSubject.next(event);
  }

  public get eventReceived$(): Observable<IRecorderInfo> {
    return this.recorderEventSubject.asObservable();
  }

  public registerPreStopHook(hook: IHook) {
    this.preStopHooks.push(hook);
  }

  private async runPreStopHooks() {
    try {
      return Promise.all(this.preStopHooks.map((hook) => hook.run()));
    } catch (ex) {
      debugError(ex);
    }
  }

  public start(options: IRecorderOptions): Promise<IRecorderResponse> {
    return this.recorderResource.create(options, {
      urlPostfix: "recordings/start",
    });
  }

  /**
   * Marks the conference the agent is in. Activations for any other conference are dropped
   * if they have not been sent yet, and their responses are ignored if they have.
   */
  public enterConference(conferenceId: string) {
    this.currentConferenceId = conferenceId;
  }

  public leaveConference(conferenceId: string) {
    if (this.currentConferenceId === conferenceId) {
      this.currentConferenceId = null;
    }
  }

  public isCurrentConference(conferenceId: string): boolean {
    return !!conferenceId && this.currentConferenceId === conferenceId;
  }

  /**
   * Starts a recording for the conference or returns its existing recorder.
   * Resolves to null if the agent left the conference before the request was sent or answered.
   */
  public activate(options: IRecorderOptions): Promise<IRecorderResponse> {
    const request = this.activationQueue.then(() =>
      this.isCurrentConference(options.conferenceId)
        ? this.recorderResource.create(options, {
            urlPostfix: "recordings/activate",
          })
        : null,
    );
    this.activationQueue = request.catch(() => undefined);

    return request.then(
      (recorder) =>
        this.isCurrentConference(options.conferenceId) ? recorder : null,
      (error) => {
        if (this.isCurrentConference(options.conferenceId)) {
          throw error;
        }
        return null;
      },
    );
  }

  /**
   * The recorder of a conference, without starting one. Null if there is none.
   */
  public async getCurrent(
    conversationId: string,
    conferenceId: string,
  ): Promise<IRecorderInfo> {
    try {
      return await this.recorderResource.get({
        urlPostfix: `recordings/${conversationId}/conferences/${conferenceId}/current`,
      });
    } catch (ex) {
      return null;
    }
  }

  public async stop(
    recorderId: string,
    conversationId: string,
    instanceId: string,
  ): Promise<void> {
    await this.runPreStopHooks();

    return this.recorderResource.create(null, {
      urlPostfix: `recordings/${conversationId}/${recorderId}/${instanceId}/stop`,
    });
  }

  public getInfo(
    recorderId: string,
    conversationId: string,
    instanceId: string,
  ): Promise<IRecorderInfo> {
    return this.recorderResource.get({
      urlPostfix: `recordings/${conversationId}/${recorderId}/${instanceId}`,
    });
  }

  public getSessionInfo(
    recorderId: string,
    conversationId: string,
    instanceId: string,
  ): Promise<IRecorderSessionInfo> {
    if (recorderId && conversationId && instanceId) {
      return this.recorderResource.get({
        urlPostfix: `recordings/${conversationId}/${recorderId}/${instanceId}/storage/info`,
      });
    }
  }

  public storeRecorderInfo(conversationId: string, recorder: IRecorderInfo) {
    // this.storage.setItem(`${KEY_RECORDER}${conversationId}`, JSON.stringify(recorder));
  }

  public clearRecorderInfo(conversationId) {
    // this.storage.removeItem(`${KEY_RECORDER}${conversationId}`);
  }

  public reset() {
    this.preStopHooks = [];
  }

  public async getStoredRecorderInfo(conversationId): Promise<IRecorderInfo> {
    try {
      const data = await this.recorderResource.get({
        urlPostfix: `recordings/${conversationId}/active`,
      });
      return data;
      // data = JSON.parse(this.storage.getItem(`${KEY_RECORDER}${conversationId}`));
      // return data;
    } catch (ex) {
      return null;
    }
  }

  public async getCobrowseRecordings(
    conversationId: string,
  ): Promise<IRecorderCobrowseInfo> {
    try {
      return this.recorderResource.get({
        urlPostfix: `recordings/${conversationId}/cobrowse`,
      });
    } catch {
      return null;
    }
  }

  public async getCobrowseRecordingUrl(
    conversationId: string,
    recorderId: string,
    instanceId: string,
    sessionId: string,
  ): Promise<IRecorderCobrowseUrl> {
    try {
      return this.recorderResource.get({
        urlPostfix: `recordings/${conversationId}/${recorderId}/${instanceId}/cobrowse/${sessionId}/url/attachment`,
      });
    } catch {
      return null;
    }
  }

  public testStorageProviderConnection(
    applicationId: string,
  ): Promise<IStorageTestSummary> {
    return this.recorderResource.create(null, {
      urlPostfix: `storageproviders/${applicationId}/verify`,
    });
  }

  public addTranscript(script: ITranscript) {
    return this.recorderResource.create(script, {
      urlPostfix: "recordings/chat/transcript/add",
    });
  }

  public getTranscript(conversationId: string): Promise<ITranscript> {
    return this.recorderResource.get({
      urlPostfix: `recordings/${conversationId}/chat/transcript`,
    });
  }

  public storageProviderConnectionSuccess(
    applicationId: string,
  ): Promise<void> {
    return this.recorderResource.create(undefined, {
      urlPostfix: `storageproviders/${applicationId}/verified`,
    });
  }

  public getStorageProviderStatus(
    applicationId: string,
  ): Promise<IStorageStatusResponse> {
    return this.recorderResource.get({
      urlPostfix: `storageproviders/${applicationId}/status`,
    });
  }
}
